import {
    QueryKey,
    QueryObserverBaseResult,
    UseMutationResult,
    useQueryClient,
} from '@tanstack/react-query'
import { ReactNode, createContext, useMemo } from 'react'
import { Outlet, useParams } from 'react-router-dom'

import {
    HTTPValidationError,
    ObjectStaticShort,
    ResponseOK,
} from '@/api/fetchers.schemas'
import { Model, ModelPatchType, ModelReturnType } from '@/config/objects/types'
import useAuth from '@/hooks/useAuth'
import { toastNotification } from '@/utils/toastNotification'

interface ObjectContextType extends QueryObserverBaseResult<ModelReturnType> {
    /** Querykey */
    queryKey: QueryKey
    /** Can be used to patch the object */
    usePatchObject: () => UseMutationResult<
        {
            Object_ID?: number
            UUID?: string
        },
        HTTPValidationError,
        {
            moduleId: number
            lineageId: number
            data: ModelPatchType
        },
        unknown
    >
    /** Can be used to patch the static data of object */
    usePostObjectStatic: (onSucces?: () => void) => UseMutationResult<
        ResponseOK,
        HTTPValidationError,
        {
            lineageId: number
            data: ObjectStaticShort
        },
        unknown
    >
    /** Is user owner of object */
    isOwner?: boolean
    /** Is user client of object */
    isClient?: boolean
}

export const ObjectContext = createContext<ObjectContextType>(null!)

function ObjectProvider({
    model,
    children,
}: {
    model: Model
    children?: ReactNode
}) {
    const queryClient = useQueryClient()
    const { user } = useAuth()

    const { moduleId, objectId } = useParams()

    const {
        useGetLatestLineage,
        useGetLatestLineageInModule,
        usePatchObjectInModule,
        usePostStatic,
    } = model.fetchers

    const latestInModule = useGetLatestLineageInModule?.(
        parseInt(moduleId!),
        parseInt(objectId!),
        {
            query: {
                enabled: !!objectId && !!moduleId,
            },
        }
    )
    const latest = useGetLatestLineage!(parseInt(objectId!), {
        query: {
            enabled: !!objectId && !moduleId,
        },
    })

    /**
     * If object is still in a module return latest lineage of object in module,
     * if object is not in a module return latest valid lineage
     */
    const data = useMemo(() => {
        if (!!moduleId && !!objectId) {
            return latestInModule
        }

        return latest
    }, [moduleId, objectId, latestInModule, latest])

    const usePatchObject = () =>
        usePatchObjectInModule?.({
            mutation: {
                onSuccess: () => toastNotification('saved'),
            },
        })

    const usePostObjectStatic = (onSuccess?: () => void) =>
        usePostStatic?.({
            mutation: {
                onSuccess: () => {
                    queryClient
                        .invalidateQueries({ queryKey: data?.queryKey })
                        .then(onSuccess)

                    toastNotification('saved')
                },
            },
        })

    /**
     * Check if logged in user is owner of object
     */
    const isOwner = useMemo(
        () =>
            data?.data?.ObjectStatics?.Owner_1?.UUID === user?.UUID ||
            data?.data?.ObjectStatics?.Owner_2?.UUID === user?.UUID,
        [
            user?.UUID,
            data?.data?.ObjectStatics?.Owner_1?.UUID,
            data?.data?.ObjectStatics?.Owner_2?.UUID,
        ]
    )

    /**
     * Check if logged in user is set as client
     */
    const isClient = useMemo(
        () => data?.data?.ObjectStatics?.Client_1?.UUID === user?.UUID,
        [user?.UUID, data?.data?.ObjectStatics?.Client_1?.UUID]
    )

    const value = {
        ...data,
        usePatchObject,
        usePostObjectStatic,
        isOwner,
        isClient,
    }

    return (
        // @ts-ignore
        <ObjectContext.Provider value={value}>
            {children || <Outlet />}
        </ObjectContext.Provider>
    )
}

export default ObjectProvider
